import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppSelector, useAppDispatch } from '../redux/hooks';
import { createAppointment, resetBooking } from '../redux/slices/bookingSlice';
import { formatAppointmentDate } from '../utils/timeUtils';
import { ServiceCard, ServiceCardSmall } from '../components/ServiceCard';
import { StylistCard, StylistCardSmall } from '../components/StylistCard';
import { BookingForm, type BookingFormData } from '../components/BookingForm';
import { BookingConfirmation } from '../components/BookingConfirmation';
import { CategoryFilter } from '../components/CategoryFilter';
import { DateTimeSelector } from '../components/DateTimeSelector';
import { BookingSummary } from '../components/BookingSummary';
import { 
  getAvailableStylistsForService,
  getAvailableServicesForStylist,
  getAvailableTimeSlots
} from '../utils/bookingUtils';

// Service interface - matches component requirements
interface Service {
  id: string;
  name: string;
  description: string;
  price: string;
  duration: number;
  image: string;
  category: string;
  availableStylists: string[];
}

interface Stylist {
  id: string;
  name: string;
  role: string;
  image: string;
  description: string;
  services: string[];
  availability: {
    [key: string]: string[];
  };
}

interface BookingData {
  service?: Service;
  stylist?: Stylist;
  date?: string;
  time?: string;
}

const BookingPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchParams] = useSearchParams();
  
  const [step, setStep] = useState(1);
  const [bookingData, setBookingData] = useState<BookingData>({});
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedCategory, setSelectedCategory] = useState<string>('all');
  const [entryPoint, setEntryPoint] = useState<'service' | 'stylist'>('service'); // Track entry point

  // Get services and stylists from Redux
  const { services: apiServices } = useAppSelector(state => state.service);
  
  // Extract unique categories dynamically from API services
  const categories = useMemo(() => {
    const uniqueCategories = new Set(
      apiServices.map((s: any) => s.category).filter(Boolean)
    );
    return ['all', ...Array.from(uniqueCategories)];
  }, [apiServices]);
  const { stylists: apiStylists } = useAppSelector(state => state.stylist);
  const { success: bookingSuccess, error: bookingError } = useAppSelector(state => state.booking);
  
  // Get selected franchise from Redux
  const { selectedFranchise } = useAppSelector(state => state.salon);
  
  // Transform API services to component format
  const services: Service[] = useMemo(() => {
    return apiServices.map((apiService: any) => ({
      id: apiService.id,
      name: apiService.name,
      description: apiService.description,
      price: `$${apiService.price}`,
      duration: apiService.duration,
      image: apiService.image_url,
      category: apiService.category,
      availableStylists: [], // Will be populated from stylist data
    }));
  }, [apiServices]);
  
  const stylists: Stylist[] = useMemo(() => {
    return apiStylists.map((apiStylist: any) => {
      // Parse availability JSON string
      let availability: { [key: string]: string[] } = {};
      try {
        availability = JSON.parse(apiStylist.availability);
      } catch (e) {
        console.error('Error parsing availability for stylist:', apiStylist.name, e);
      }
      
      // Parse specialization - handle both JSON array and comma-separated formats
      let stylistServices: string[] = [];
      if (apiStylist.specialization) {
        try {
          // Try parsing as JSON array first (most common format)
          stylistServices = JSON.parse(apiStylist.specialization);
        } catch {
          // If JSON parse fails, try comma-separated format
          stylistServices = apiStylist.specialization
            .split(',')
            .map((id: string) => id.trim())
            .filter(Boolean);
        }
      }
      
      return {
        id: apiStylist.id,
        name: apiStylist.name,
        role: apiStylist.level, // Show only level, not specialization
        image: apiStylist.image_url,
        description: apiStylist.bio,
        services: stylistServices,
        availability,
      };
    });
  }, [apiStylists, services]);

  // Reset booking state when services or stylists data loads/changes
  useEffect(() => {
    // Only reset if we have data and we're not in critical steps (form filling or confirmation)
    if ((apiServices.length > 0 || apiStylists.length > 0) && step < 4) {
      console.log('🔄 Services/Stylists loaded - resetting booking state');
      resetAllState();
    }
  }, [apiServices.length, apiStylists.length]);

  // Check for pre-selected service or stylist from URL params
  useEffect(() => {
    // Reset all booking state when location/URL params change
    setBookingData({});
    setStep(1);
    setSelectedDate(new Date());
    setSelectedCategory('all');

    const entry = searchParams.get('entry');
    const serviceId = searchParams.get('service');
    const stylistId = searchParams.get('stylist');

    // Determine entry point
    if (entry === 'stylist' || (stylistId && !serviceId)) {
      setEntryPoint('stylist');
      
      if (stylistId) {
        const stylist = stylists.find(s => s.id === stylistId);
        if (stylist) {
          setBookingData({ stylist });
          // Stay at step 1 - user can see pre-selected stylist
        }
      }
    } else {
      setEntryPoint('service');
      
      if (serviceId) {
        const service = services.find(s => s.id === serviceId);
        if (service) {
          setBookingData({ service });
          // Stay at step 1 - user can see pre-selected service
        }
      }
    }
  }, [searchParams, services, stylists]);

  // Memoize filtered lists to ensure React tracks dependencies and re-renders correctly
  const availableStylists = useMemo(() => {
    return getAvailableStylistsForService(bookingData.service, stylists);
  }, [bookingData.service, stylists]);

  const availableServices = useMemo(() => {
    return getAvailableServicesForStylist(bookingData.stylist, services, selectedCategory);
  }, [bookingData.stylist, services, selectedCategory]);

  const availableTimes = useMemo(() => {
    return getAvailableTimeSlots(bookingData.stylist, selectedDate, selectedFranchise);
  }, [bookingData.stylist, selectedDate, selectedFranchise]);

  // ============ Selection Handlers ============
  
  const handleServiceSelect = (service: Service) => {
    setBookingData(prev => ({ ...prev, service }));
    // Only navigate to step 2 if we're currently at step 1
    if (step === 1) {
      goToStep2();
    }
    // If already at step 2, just update the selection (no navigation needed)
  };

  const handleStylistSelect = (stylist: Stylist) => {
    setBookingData(prev => ({ ...prev, stylist }));
    // Only navigate if we're at step 1 and entry point is stylist
    if (step === 1 && entryPoint === 'stylist') {
      goToStep2();
    }
    // If already at step 2, just update the selection (no navigation needed)
  };

  const handleTimeSelect = (time: string) => {
    setBookingData(prev => ({ 
      ...prev, 
      date: selectedDate.toISOString(),
      time 
    }));
    // Don't auto-navigate - user will click Next button
  };

  // Validation: Check if user can proceed to Step 4
  const canProceedToStep4 = () => {
    if (entryPoint === 'stylist') {
      // Stylist-first: Need service + time
      return bookingData.service && bookingData.time;
    } else {
      // Service-first: Need only time (stylist optional)
      return bookingData.time;
    }
  };

  const handleFormSubmit = (formData: BookingFormData) => {
    if (!bookingData.service || !bookingData.date || !bookingData.time) {
      console.error('Missing required booking data');
      return;
    }

      // Parse price - handle both string and number
      const priceValue = typeof bookingData.service.price === 'string' 
        ? parseFloat(bookingData.service.price.replace('$', ''))
        : bookingData.service.price;
      
      // Dispatch create appointment action with new API format
      dispatch(createAppointment({
        customer_name: formData.name,
        customer_contact: formData.phone || formData.email || '',
        customer_email: formData.email || '',
        gender: formData.gender,
        stylist_id: bookingData.stylist?.id ? [bookingData.stylist.id] : [],  // Array format
        service_id: [bookingData.service.id],  // Array format
        appointment_date: formatAppointmentDate(bookingData.date),
        start_time: bookingData.time,
        status: 'Booked',
        notes: formData.notes || '',
        discount: 0,
        price: priceValue,
      }));
  };

  // Handle booking success
  useEffect(() => {
    if (bookingSuccess) {
      setStep(5);
      // Don't auto-reset - let user click "Return to Home" button
    }
  }, [bookingSuccess, dispatch]);

  // Handle booking error
  useEffect(() => {
    if (bookingError) {
      alert(`Booking failed: ${bookingError}`);
    }
  }, [bookingError]);

  // ============ Navigation Functions ============
  
  // Step navigation helpers
  const goToStep2 = () => setStep(2);
  const goToStep4 = () => setStep(4);

  // Reset all state - ONLY called after successful booking
  const resetAllState = () => {
    setBookingData({});
    setStep(1);
    setSelectedDate(new Date());
    setSelectedCategory('all');
  };

  // Back navigation handlers
  const handleBackFromStep1 = () => navigate('/');
  
  const handleBackFromStep2 = () => {
    // Clear time and date selections when going back
    setBookingData(prev => ({
      ...prev,
      time: undefined,
      date: undefined,
      // Clear the secondary selection based on entry point
      // Service-first: clear stylist, keep service
      // Stylist-first: clear service, keep stylist
      ...(entryPoint === 'service' ? { stylist: undefined } : { service: undefined })
    }));
    
    // Reset date to today
    setSelectedDate(new Date());
    
    // Go back to step 1
    setStep(1);
  };
  
  const handleBackFromStep4 = () => goToStep2();

  // Main back handler - delegates to specific step handlers
  const handleBack = () => {
    switch (step) {
      case 1: handleBackFromStep1(); break;
      case 2: handleBackFromStep2(); break;
      case 4: handleBackFromStep4(); break;
      default: setStep(step - 1);
    }
  };

  return (
      <div className="bg-white dark:bg-custom-dark min-h-screen">
        {/* Main Content - Two Column Layout */}
        <div className="pt-32 pb-20 px-4 sm:px-6 md:px-10 xl:px-16 min-h-screen">
          <div className="max-w-7xl mx-auto">
            {step < 5 ? (
              <div className={`grid grid-cols-1 ${step >= 4 ? 'lg:grid-cols-12' : ''} gap-8`}>
                {/* Left Column - Form Section */}
                <div className={step >= 4 ? 'lg:col-span-8' : ''}>
                  <div className="px-8 pb-8">
                    {/* Top Back Button - All Steps */}
                    <button
                      onClick={handleBack}
                      className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-primary dark:hover:text-primary transition-colors mb-3 group"
                    >
                      <span className="material-symbols-outlined text-xl group-hover:-translate-x-1 transition-transform">
                        arrow_back
                      </span>
                      <span className="font-medium">Back</span>
                    </button>

                    {step === 1 && (
                      <>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                          {entryPoint === 'service' ? 'Services' : 'Stylists'}
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-8">
                          {entryPoint === 'service' ? 'Choose from our range of professional services' : 'Select your preferred stylist'}
                        </p>
                      </>
                    )}
                    {step === 2 && entryPoint === 'service' && (
                      <>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                          Select Stylist & Time
                        </h1>
                      </>
                    )}
                    {step === 2 && entryPoint === 'stylist' && (
                      <>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                          Select Service & Time
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-8">
                          Choose a service and pick your preferred date and time
                        </p>
                      </>
                    )}
                    {step === 4 && (
                      <>
                        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
                          Your Details
                        </h1>
                        <p className="text-gray-600 dark:text-gray-400 mb-8">
                          Fill in your contact information
                        </p>
                      </>
                    )}

                    <AnimatePresence mode="wait">
                      {/* Step 1: Service or Stylist Selection based on entry point */}
                      {step === 1 && entryPoint === 'service' && (
                        <motion.div
                          key="step1-service"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                        >
                          <CategoryFilter
                            categories={categories}
                            selectedCategory={selectedCategory}
                            onCategoryChange={setSelectedCategory}
                          />

                          {/* Services Grid */}
                          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                            {availableServices.map((service) => (
                              <ServiceCard
                                key={service.id}
                                service={service}
                                onSelect={handleServiceSelect}
                                buttonText="Book Now"
                              />
                            ))}
                          </div>
                        </motion.div>
                      )}

                      {/* Step 1: Stylist Selection (when entry point is stylist) */}
                      {step === 1 && entryPoint === 'stylist' && (
                        <motion.div
                          key="step1-stylist"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4"
                        >
                          {stylists.map((stylist) => (
                            <StylistCard
                              key={stylist.id}
                              stylist={stylist}
                              onSelect={handleStylistSelect}
                              buttonText="Book Now"
                            />
                          ))}
                        </motion.div>
                      )}

                      {/* Step 2: Combined Stylist + Date/Time Selection (when entry point is service) */}
                      {step === 2 && entryPoint === 'service' && (
                        <motion.div
                          key="step2-combined"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                        >
                          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                            {/* Left: Stylist Selection (Optional) - 60% */}
                            <div className="lg:col-span-7">
                              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                                Choose a stylist (Optional)
                              </p>
                              <div className="grid grid-cols-3 gap-3 max-h-[600px] overflow-y-auto p-1">
                                {availableStylists.map((stylist) => (
                                  <StylistCardSmall
                                    key={stylist.id}
                                    stylist={stylist}
                                    onSelect={handleStylistSelect}
                                    buttonText={bookingData.stylist?.id === stylist.id ? "Selected" : "Select"}
                                  />
                                ))}
                              </div>
                            </div>

                            {/* Right: Date & Time Selection - 40% */}
                            <div className="lg:col-span-5">
                              <DateTimeSelector
                                selectedDate={selectedDate}
                                onDateChange={setSelectedDate}
                                availableTimes={availableTimes}
                                onTimeSelect={handleTimeSelect}
                              />
                              
                              {/* Next Button - Service-first flow */}
                              <div className="mt-6 flex justify-end">
                                <button
                                  onClick={goToStep4}
                                  disabled={!canProceedToStep4()}
                                  className={`py-3 px-8 rounded-lg font-semibold text-base transition-all ${
                                    canProceedToStep4()
                                      ? 'bg-primary text-white hover:bg-primary/90 hover:scale-105'
                                      : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed opacity-50'
                                  }`}
                                >
                                  Next →
                                </button>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {/* Step 2: Combined Service + Date/Time Selection (when entry point is stylist) */}
                      {step === 2 && entryPoint === 'stylist' && (
                        <motion.div
                          key="step2-stylist-combined"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                        >
                          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
                            {/* Left: Service Selection - 60% */}
                            <div className="lg:col-span-7">
                              <CategoryFilter
                                categories={categories}
                                selectedCategory={selectedCategory}
                                onCategoryChange={setSelectedCategory}
                              />

                              {/* Services Grid */}
                              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[600px] overflow-y-auto p-1">
                                {availableServices.map((service) => (
                                  <ServiceCardSmall
                                    key={service.id}
                                    service={service}
                                    onSelect={handleServiceSelect}
                                    buttonText={bookingData.service?.id === service.id ? "Selected" : "Select"}
                                  />
                                ))}
                              </div>
                            </div>

                            {/* Right: Date & Time Selection - 40% */}
                            <div className="lg:col-span-5">
                              <DateTimeSelector
                                selectedDate={selectedDate}
                                onDateChange={setSelectedDate}
                                availableTimes={availableTimes}
                                onTimeSelect={handleTimeSelect}
                              />
                              
                              {/* Next Button - Stylist-first flow */}
                              <div className="mt-6 flex justify-end">
                                <button
                                  onClick={goToStep4}
                                  disabled={!canProceedToStep4()}
                                  className={`py-3 px-8 rounded-lg font-semibold text-base transition-all ${
                                    canProceedToStep4()
                                      ? 'bg-primary text-white hover:bg-primary/90 hover:scale-105'
                                      : 'bg-gray-300 dark:bg-gray-700 text-gray-500 dark:text-gray-500 cursor-not-allowed opacity-50'
                                  }`}
                                >
                                  Next →
                                </button>
                              </div>
                            </div>
                          </div>
                        </motion.div>
                      )}

                      {/* Step 4: Customer Details */}
                      {step === 4 && (
                        <motion.div
                          key="step4"
                          initial={{ opacity: 0, y: 20 }}
                          animate={{ opacity: 1, y: 0 }}
                          exit={{ opacity: 0, y: -20 }}
                          className="bg-[#202022] rounded-2xl shadow-xl p-8"
                        >
                          <BookingForm onSubmit={handleFormSubmit} />
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </div>

                {/* Right Column - Booking Summary Card - Only show from Step 4 onwards */}
                {step >= 4 && (
                  <div className="lg:col-span-4">
                    <BookingSummary
                      bookingData={bookingData}
                      step={step}
                      onBack={step < 4 ? handleBack : undefined}
                    />
                  </div>
                )}
              </div>
            ) : (
              /* Step 5: Confirmation */
              <BookingConfirmation onReturnHome={() => {
                dispatch(resetBooking());
                resetAllState();
                navigate('/');
              }} />
            )}
          </div>
        </div>
      </div>
  );
};

export default BookingPage;
